import { Badge, Box, Button, Card, Grid, Text } from '@mantine/core';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { setKlaviyoAccount } from '../../store/klaviyoSlice';
import ErrorMessage from '../ErrorMessage';

const BACKFILL_ERROR_FALLBACK = 'Could not start the Klaviyo profile import. Please try again or contact support.';

// Whether the API has recorded anything about a crawl. Null markers (a row the API
// initialises as { checkpoint: null, cursor: null }) do not count as a started import.
const hasRecordedState = (profileSyncState) =>
  Object.values(profileSyncState || {}).some((value) => value !== null && value !== undefined);

const FAILURE_NOTE = 'The last run reported an error - check the API logs if the progress above stops advancing.';

// Progress and failure are not mutually exclusive. A bounded crawl that hits a Klaviyo 429
// or a timeout can record the failure and re-enqueue itself, so a persisted error is a
// "last failure we saw" field for all this side knows - letting it outrank a cursor or a
// fresh checkpoint would pin the badge red and send an admin to the API logs for a healthy
// account. It therefore rides alongside the real status as a warning, and only owns the
// badge when there is no progress at all to report.
//
// Derive a human status from profileSyncState (see jobs/klaviyo-profiles.js in the API):
// - a cursor means a crawl is mid-flight (bounded runs re-enqueue themselves until the
//   audience is covered);
// - a checkpoint with no cursor means the crawl completed and incremental runs keep it
//   current from there;
// - a persisted error with neither of those means the import never got anywhere and the
//   failure is the whole story;
// - an explicit kickoff marker (requestedAt/crawlStartedAt) with none of the above means
//   the initial crawl is queued but has not reported progress yet;
// - an empty object - or one the API initialised with nothing but null markers - means the
//   import was never started.
// Anything else is a shape we do not recognise (a field added on the API side, a
// half-written row) - say so rather than reporting progress that may not be happening.
const backfillStatus = (profileSyncState) => {
  const state = profileSyncState || {};
  // Both spellings are accepted because which one the API persists is not pinned down on
  // this side; an unused branch here costs nothing, a missed failure costs a support ticket.
  const warning = state.lastError || state.error ? FAILURE_NOTE : undefined;
  if (state.cursor) {
    return { color: 'yellow', label: 'Import in progress', warning };
  }
  if (state.checkpoint) {
    return {
      color: 'green',
      label: `Imported - profiles current as of ${new Date(state.checkpoint).toLocaleString()}`,
      warning,
    };
  }
  if (warning) {
    return { color: 'red', label: 'Import failed - check the API logs or contact support' };
  }
  if (state.requestedAt || state.crawlStartedAt) {
    return { color: 'yellow', label: 'Initial import running' };
  }
  if (hasRecordedState(state)) {
    return { color: 'gray', label: 'Status unknown' };
  }
  return { color: 'gray', label: 'Not started' };
};

// One-click import of the client's Klaviyo audience: profiles Releva has never seen
// become targetable profiles (names + subscribe/unsubscribe consent state included),
// and after the initial crawl the import keeps itself current incrementally. Re-running
// is always safe - profile upserts are idempotent and consent is timestamp-guarded.
const KlaviyoBackfillCard = ({ klaviyoAccount }) => {
  const fetch = useFetch();
  const dispatch = useDispatch();
  const { accountId, domainId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  // The "refresh for status" hint belongs to one specific sync state: the one that was in
  // place when the enqueue was confirmed. As soon as the API reports anything different the
  // badge speaks for itself, and the hint would be a second, stale instruction beside it.
  const [requestedFor, setRequestedFor] = useState();

  const started = hasRecordedState(klaviyoAccount.profileSyncState);
  const status = backfillStatus(klaviyoAccount.profileSyncState);
  const syncStateKey = JSON.stringify(klaviyoAccount.profileSyncState ?? null);
  const requested = requestedFor === syncStateKey;

  const startBackfill = async () => {
    setError(undefined);
    setRequestedFor(undefined);
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/klaviyo/backfill`, {
        method: 'POST',
      });
      if (response?.profileSyncState) {
        dispatch(setKlaviyoAccount({ ...klaviyoAccount, profileSyncState: response.profileSyncState }));
      } else {
        // "Job enqueued" is naturally a 202, for which useFetch resolves `{}` - confirm the
        // click locally instead of leaving the badge on its old value with no feedback.
        setRequestedFor(syncStateKey);
      }
    } catch (e) {
      setError(getErrorMessage(e, BACKFILL_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Profile import
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Text size="sm" c="dimmed" mb="xs">
              Imports Klaviyo profiles into Releva with their names and email consent, then keeps them in sync. Runs in
              the background - refresh the page for status. Safe to run repeatedly.
            </Text>

            <Box mb="xs">
              <Badge color={status.color} variant="light">
                {status.label}
              </Badge>
              {status.warning && (
                <Text size="sm" c="orange" mt={4}>
                  {status.warning}
                </Text>
              )}
            </Box>

            <Button variant="default" onClick={startBackfill} loading={loading}>
              {started ? 'Run import now' : 'Import Klaviyo profiles'}
            </Button>

            {requested && (
              <Text size="sm" c="green" mt="xs">
                Import requested - refresh in a minute for status.
              </Text>
            )}

            <ErrorMessage message={error} />
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default KlaviyoBackfillCard;
